import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import {
  User,
  Mail,
  Lock,
  Bell,
  Eye,
  Shield,
  Trash2
} from 'lucide-react';
import { useApp } from '@/pages/providers/AppProvider';
import { toast } from 'sonner';

export function SettingsSection() {
  const { currentUser } = useApp();

  const handleSave = () => {
    toast.success('Settings saved successfully!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
        <p className="text-gray-600 mt-1">Manage your account preferences and security</p>
      </div>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name</Label>
              <Input defaultValue={currentUser?.name || ''} />
            </div>
            <div>
              <Label>Email Address</Label>
              <Input defaultValue={currentUser?.email || ''} type="email" />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input placeholder="+977 98XXXXXXXX" />
            </div>
            <div>
              <Label>Location</Label>
              <Input placeholder="Kathmandu, Nepal" />
            </div>
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea
              placeholder="Tell us about yourself..."
              rows={3}
            />
          </div>
          <div>
            <Label>LinkedIn Profile</Label>
            <Input placeholder="https://linkedin.com/in/yourprofile" />
          </div>
          <Button onClick={handleSave}>Save Changes</Button>
        </CardContent>
      </Card>

      {/* Password & Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Password & Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Current Password</Label>
            <Input type="password" placeholder="Enter current password" />
          </div>
          <div>
            <Label>New Password</Label>
            <Input type="password" placeholder="Enter new password" />
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input type="password" placeholder="Confirm new password" />
          </div>
          <Button>Update Password</Button>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-gray-500">Receive job alerts via email</p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Application Updates</p>
              <p className="text-sm text-gray-500">Get notified about application status changes</p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Job Recommendations</p>
              <p className="text-sm text-gray-500">Receive personalized job suggestions</p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Marketing Emails</p>
              <p className="text-sm text-gray-500">Receive newsletters and updates</p>
            </div>
            <Switch />
          </div>
        </CardContent>
      </Card>

      {/* Privacy Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Privacy Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Profile Visibility</p>
              <p className="text-sm text-gray-500">Make your profile visible to employers</p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Show Application History</p>
              <p className="text-sm text-gray-500">Let employers see your past applications</p>
            </div>
            <Switch />
          </div>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Anonymous Job Search</p>
              <p className="text-sm text-gray-500">Hide your profile from current employer</p>
            </div>
            <Switch />
          </div>
        </CardContent>
      </Card>

      {/* Job Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Job Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Preferred Job Types</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {['Full-time', 'Part-time', 'Contract', 'Remote', 'Internship', 'Freelance'].map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <input type="checkbox" id={type} className="rounded" />
                  <label htmlFor={type} className="text-sm">{type}</label>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Expected Salary Range</Label>
            <div className="grid grid-cols-2 gap-4">
              <Input placeholder="Min (NPR)" />
              <Input placeholder="Max (NPR)" />
            </div>
          </div>
          <div>
            <Label>Preferred Locations</Label>
            <Input placeholder="e.g., Kathmandu, Pokhara, Remote" />
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border border-red-200 rounded-lg bg-red-50">
            <h4 className="font-medium text-red-900 mb-2">Delete Account</h4>
            <p className="text-sm text-red-700 mb-4">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <Button variant="destructive">Delete My Account</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
